import React, { FC, useCallback, useMemo } from 'react';
import { Button, Divider, Flex } from 'antd';
import styled from 'styled-components';
import { colorGray } from '@atlas-frontend/design-tokens';
import { useFormContext } from 'react-hook-form';
import { useSystemClassifiers } from '../../../configuration/useSystemClassifiers';
import {
  AlgorithmElementFunctionType,
  AlgorithmElementFunctionViewType,
  DataObjectType,
} from '../../types';
import translations from './AlgorithmConditionForm.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { DataObjectDescriptionVersionStore } from '../../DataObjectDescriptionVersionStore';

const { addTranslations } = i18nNsFactory('algorithm-condition-form');
addTranslations(translations);

export interface AlgorithmConditionElementFunctionItemsProps {
  fieldName: string;
  viewType: AlgorithmElementFunctionViewType;
}

export interface AlgorithmElementFunctionItem {
  type: AlgorithmElementFunctionType;
  value: string;
  view: string[];
  hideForDataObjectType?: DataObjectType;
}

export const AlgorithmConditionElementFunctionItems: FC<
  AlgorithmConditionElementFunctionItemsProps
> = (props) => {
  const { fieldName: name, viewType } = props;
  const { getValues, setValue } = useFormContext();

  const { ALGORITHM_ELEMENT_FUNCTION } = useSystemClassifiers(
    'ALGORITHM_ELEMENT_FUNCTION',
  );

  const [dataObjectDescriptionVersion] =
    DataObjectDescriptionVersionStore.useValue('dataObjectDescriptionVersion');

  const elementFunctionItems = useMemo(
    () =>
      ALGORITHM_ELEMENT_FUNCTION.systemClassifierItems
        .sort(
          (a, b) =>
            (a.sequenceNumber ?? Number.MAX_SAFE_INTEGER) -
            (b.sequenceNumber ?? Number.MAX_SAFE_INTEGER),
        )
        .filter((item) => Boolean(item.parameters))
        .map((item) => {
          try {
            return JSON.parse(item.parameters);
          } catch (err) {
            console.error(err);
          }
          return;
        })
        .filter((item: AlgorithmElementFunctionItem) => {
          if (
            item.hideForDataObjectType &&
            item.hideForDataObjectType ===
              dataObjectDescriptionVersion?.dataObjectType
          ) {
            return false;
          }
          return item?.view.includes(viewType);
        }),
    [
      ALGORITHM_ELEMENT_FUNCTION,
      dataObjectDescriptionVersion?.dataObjectType,
      viewType,
    ],
  );

  const replaceSelectionWithOperator = useCallback(
    (item: AlgorithmElementFunctionItem) => {
      const inputElement = document.getElementById(
        'algorithm-condition-' + name,
      ) as HTMLTextAreaElement;
      const selectionStart = inputElement.selectionStart;
      const value = getValues(name);
      let newSelectStart = selectionStart;
      let newSelectEnd = selectionStart;
      if (item.type === AlgorithmElementFunctionType.FUNCTION) {
        const selectionEnd = inputElement.selectionEnd;
        setValue(
          name,
          value.slice(0, selectionStart) +
            item.value +
            '(' +
            value.slice(selectionStart, selectionEnd) +
            ')' +
            value.slice(selectionEnd),
          { shouldDirty: true },
        );
        newSelectStart = selectionStart + item.value.length + 1;
        newSelectEnd = selectionEnd + item.value.length + 1;
      } else {
        setValue(
          name,
          value.slice(0, selectionStart) +
            item.value +
            value.slice(selectionStart),
          { shouldDirty: true },
        );
        newSelectStart = selectionStart + item.value.length;
        newSelectEnd = newSelectStart;
      }
      inputElement.focus();
      //workaround
      window.setTimeout(function () {
        inputElement.setSelectionRange(newSelectStart, newSelectEnd);
      }, 0);
    },
    [name, getValues, setValue],
  );

  return (
    <Flex gap="small" wrap="wrap">
      {elementFunctionItems
        .filter((item) => item.type !== AlgorithmElementFunctionType.FUNCTION)
        .map((item, index) => {
          return (
            <OperatorButtonWrapper
              id={'element-item-' + index}
              key={'element-item-' + index}
              type="default"
              onClick={() => replaceSelectionWithOperator(item)}
            >
              {item.value}
            </OperatorButtonWrapper>
          );
        })}
      <StyledDivider type="vertical" />
      {elementFunctionItems
        .filter((item) => item.type === AlgorithmElementFunctionType.FUNCTION)
        .map((item, index) => {
          return (
            <Button
              id={'function-item-' + index}
              key={'element-item-' + index}
              type="default"
              onClick={() => replaceSelectionWithOperator(item)}
            >
              {item.value}
            </Button>
          );
        })}
    </Flex>
  );
};

export const OperatorButtonWrapper = styled(Button)`
  background-color: ${colorGray[2]};
  border-color: ${colorGray[3]};
`;

const StyledDivider = styled(Divider)`
  height: 32px;
`;
